import { css, cx } from '@linaria/core'
import { createRef, type FC, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'
import { Button } from '#components/Button'
import { Icon } from '#components/Icon'
import { IconButton } from '#components/IconButton'
import { Modal } from '#components/Modal'
import { authRouteMatcher } from '#features/User/buildAuthRouteFragments'
import CloseIcon from '#icons/close-16.svg'
import PlayIcon from '#icons/play-48.svg'
import { setWelcomeOverlayDismissed } from '#store/componentConfiguration'
import { useSharedAppDispatch, useSharedAppSelector } from '#store/sharedBaseStore'
import { fragments } from '#styles'
import { themeColors } from '#styles/themes'

const backdrop = css`
  // above other modals
  z-index: 10;
  
  > ._dialog {
    padding: 0 24px;
    min-width: 512px;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    overflow-y: auto;

    > header {
      position: sticky;
      top: 0;
      padding: 24px 0 24px 0;
      display: flex;
      z-index: 1;
      justify-content: space-between;
      background-color: ${themeColors.backgroundDefault};

      > h2 > .reduced {
        ${fragments.textStyle.heading.l.subtle}
      }

      > .icon.accent {
        color: ${themeColors.textImportant};
      }
    }

    > main {
      flex: 1;
      position: relative;
      display: flex;
      flex-direction: column;
      gap: 24px;
      align-items: flex-start;

      > .video-container {
        max-height: 40vh;
        aspect-ratio: 16 / 9;
        align-self: center;
        position: relative;

        > video {
          max-height: 40vh;
          aspect-ratio: 16 / 9;
          // fill the container with the poster
          object-fit: cover;
        }

        > .play-icon {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          padding: 10px 10px 10px 15px;
          background-color: ${themeColors.backgroundAccent};
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
        }

        &.playing {
          
          > video {
            object-fit: contain;
          }
          
          > .play-icon {
            display: none;
          }
        }

        &:hover { 
          > .play-icon {
            background-color: ${themeColors.backgroundAccentHover};
          }
        }
      
      }

      > p {
        white-space: pre-wrap;
      }
    }

    > footer {
      display: flex;
      justify-content: flex-end;
      flex-direction: row;
      gap: 8px;
      position: sticky;
      bottom: 0;
      padding: 24px 0 24px 0;
      background-color: ${themeColors.backgroundPopout};
    }
  }
`

export const WelcomeOverlay: FC = () => {
  const { t } = useTranslation('shared')
  const dispatch = useSharedAppDispatch()

  const location = useLocation()
  const isLoginRoute = location.pathname.match(authRouteMatcher) !== null
  const welcomeState = useSharedAppSelector((state) => state.componentConfiguration.welcomeOverlay)

  const dismissHandler = useCallback(() => {
    dispatch(setWelcomeOverlayDismissed(VITE_APP_VERSION))
  }, [dispatch])

  const isDismissed = welcomeState?.lastDismissedVersion === VITE_APP_VERSION
  const isOpen = !isDismissed && !isLoginRoute
  const videoRef = createRef<HTMLVideoElement>()

  return (
    <Modal open={isOpen} lifted className={cx(backdrop)} onOpenChange={() => dismissHandler()} dismissOnOutside={false} dataTestId="welcome-overlay">
      <header>
        <h2>
          {t('welcome.title', 'How to contribute to MAIC early access')}
          <span className="reduced"> - {VITE_APP_VERSION}</span>
        </h2>
        <IconButton iconSize="m" className="icon accent" iconUrl={CloseIcon} title={t('common.button.close')} onClick={() => dismissHandler()} />
      </header>
      <main className="_content">
        <div
          className="video-container"
          onClick={(evt) => {
            evt.currentTarget.classList.add('playing')

            if (videoRef.current) {
              if (videoRef.current.paused) {
                void videoRef.current.play()
              }
            }
          }}
        >
          <video
            ref={videoRef}
            src="/images/welcome/welcome.webm"
            poster="/images/welcome/poster.png"
            onPlay={(e) => {
              e.currentTarget.controls = true
            }}
          />
          <Button className="play-icon">
            <Icon iconUrl={PlayIcon} size="xl" />
          </Button>
        </div>
        <p>
          {t(
            'welcome.description',
            'Welcome to MAIC! The truck configurator for the TMS platform is under active development.\nWe are excited to have you on board and would love your feedback.',
          )}
        </p>
      </main>
      <footer>
        <a target="_blank" rel="noopener noreferrer" href="https://confluence.collaboration-man.com/display/MANC4TMS/What%27s+New">
          <Button kind="secondary">{t('welcome.button.whats-new', "What's new?")}</Button>
        </a>
        <a target="_blank" rel="noopener noreferrer" href="https://confluence.collaboration-man.com/display/MANC4TMS/Documentation">
          <Button kind="secondary">{t('welcome.button.documentation', 'MAIC Documentation')}</Button>
        </a>
        <Button kind="accent" onClick={() => dismissHandler()} data-testid="welcome-overlay-dismiss">
          {t('welcome.button.dismiss', 'Got it')}
        </Button>
      </footer>
    </Modal>
  )
}
